import React, { useMemo } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { useGetAllGroupsQuery, useUserTokenInfoQuery } from 'graphql/types';
import { LeftOutlined } from '@ant-design/icons';
import ReactHtmlParser from 'react-html-parser';
import GroupHero from './components/GroupHero';
import GroupEventsSection from './components/GroupEventsSection';
import GroupSidebar from './components/GroupSidebar';
import type { CreateEventPrefillState } from 'containers/account/createUpdateEvent/createEventPrefill';
import { LeftColumn, MainGrid, PageLayout, PageWrapper, BackButton, CardHeader, SectionTitle, AboutText } from './index.styled';

type MatchParams = { slug: string };
type SingleGroupProps = RouteComponentProps<MatchParams>;

export default function SingleGroup(props: SingleGroupProps) {
  const {
    history,
    match: {
      params: { slug },
    },
  } = props;
  const { data: groupByIdSlugResult } = useGetAllGroupsQuery({
    variables: { filters: { slug: { eq: slug } } },
  });
  const { data: userTokenInfoResult } = useUserTokenInfoQuery();

  const selectedGroupResult = useMemo(() => {
    return groupByIdSlugResult?.groups.data && groupByIdSlugResult?.groups.data.length
      ? groupByIdSlugResult?.groups.data[0]
      : null;
  }, [groupByIdSlugResult]);

  const selectedGroupAttribute = selectedGroupResult?.attributes;
  const members = selectedGroupAttribute?.members?.data || [];
  const organizers = selectedGroupAttribute?.organizers?.data || [];
  const managers = selectedGroupAttribute?.managers?.data || [];
  const events = selectedGroupAttribute?.events?.data || [];
  const upcomingEvents = useMemo(() => events.filter((event) => !event?.attributes?.isPast), [events]);
  const pastEvents = useMemo(() => events.filter((event) => !!event?.attributes?.isPast), [events]);
  const groupLocation = selectedGroupAttribute?.address?.data?.attributes?.city || 'Online';
  const currentUserId = userTokenInfoResult?.userTokenInfo?.id;
  const isCurrentUserOrganizer = useMemo(() => {
    if (!currentUserId) {
      return false;
    }
    return organizers.some((organizer) => String(organizer?.id) === String(currentUserId));
  }, [currentUserId, organizers]);

  const handleCreateEvent = () => {
    if (!selectedGroupResult?.id) {
      return;
    }
    const prefill: CreateEventPrefillState = {
      groupId: String(selectedGroupResult.id),
      categoryIds: (selectedGroupAttribute?.categories?.data ?? []).map((category) => String(category.id)),
      coverPhotoUrl: selectedGroupAttribute?.coverPhotoUrl || undefined,
    };
    history.push({ pathname: '/events/create', state: prefill });
  };

  return (
    <PageWrapper>
      <PageLayout>
        <BackButton onClick={() => history.goBack()}>
          <LeftOutlined />
          Back
        </BackButton>
        <MainGrid>
          <LeftColumn>
            <GroupHero
              name={selectedGroupAttribute?.name}
              coverPhotoUrl={selectedGroupAttribute?.coverPhotoUrl}
              location={groupLocation}
              memberCount={members.length}
              groupId={selectedGroupResult?.id}
              showManageButtons={isCurrentUserOrganizer}
              showJoinGroupButton={!isCurrentUserOrganizer}
              onEdit={() => history.push(`/groups/update/${selectedGroupResult?.id}`)}
              onManage={() => history.push(`/groups/manage/${selectedGroupResult?.id}`)}
              onCreateEvent={handleCreateEvent}
            />
            <CardHeader>
              <SectionTitle>About This Group</SectionTitle>
              <AboutText>{ReactHtmlParser(selectedGroupAttribute?.description || '')}</AboutText>
            </CardHeader>
          </LeftColumn>

          <GroupSidebar
            organizers={organizers}
            members={members}
            managers={managers}
            totalEvents={events.length}
            upcomingEvents={upcomingEvents.length}
          />
        </MainGrid>
        <GroupEventsSection upcomingEvents={upcomingEvents} pastEvents={pastEvents} />
      </PageLayout>
    </PageWrapper>
  );
}
